// src/accessControl.js
import { ref, get } from "firebase/database";
import { db } from "./firebaseConfig";
import { auth } from "./firebaseConfig";

// Convert email into a Firebase-safe key (no dots allowed)
function safeEmailKey(email) {
  return (email || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, ",");
}

// 7-day cache to support offline mode (604800000 ms = 7 days)
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export async function checkAccess(email, tourName) {
  if (!email || !tourName) return false;

  // Security: Verify this is the current authenticated user's email
  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.error("Access check failed: No authenticated user");
    return false;
  }

  // Security: Only allow checking access for the currently logged-in user
  if (currentUser.email?.toLowerCase() !== email.toLowerCase()) {
    console.error("Access check failed: Email mismatch with authenticated user");
    return false;
  }

  const uid = currentUser.uid;
  const emailKey = safeEmailKey(email);
  const cacheKey = `access:${uid}:${tourName}`;
  const cached = localStorage.getItem(cacheKey); // Changed to localStorage for persistence

  // Check cache first
  if (cached) {
    try {
      const { value, at } = JSON.parse(cached);
      const cacheAge = Date.now() - at;
      const isValid = cacheAge < TTL_MS;

      // If cache is still valid, use it (whether online or offline)
      if (isValid) {
        console.log(`[AccessControl] Using cached access (${Math.floor(cacheAge / 1000 / 60)} min old)`);
        return value;
      }
    } catch (e) {
      console.error("[AccessControl] Cache parse error:", e);
    }
  }

  // If we're offline and cache is missing/expired, we can't verify
  if (!navigator.onLine) {
    console.error("[AccessControl] Offline with no valid cache - cannot verify access");
    return false;
  }

  // Online: Check Firebase and update cache
  try {
    // First try UID-based access (more secure, new method)
    let allowed = false;

    const uidAccessRef = ref(db, `userAccess/${uid}/${tourName}`);
    const uidSnapshot = await get(uidAccessRef);
    const uidData = uidSnapshot.val();

    if (uidData && uidData.granted === true) {
      allowed = true;
    } else {
      // Fallback to email-based access (legacy, for backward compatibility)
      const emailAccessRef = ref(db, `validBuyers/${emailKey}/${tourName}`);
      const emailSnapshot = await get(emailAccessRef);
      const emailData = emailSnapshot.val();
      allowed = emailData && emailData.granted === true;
    }

    // Cache the result in localStorage
    localStorage.setItem(
      cacheKey,
      JSON.stringify({ value: allowed, at: Date.now() })
    );

    console.log(`[AccessControl] Firebase check complete - access ${allowed ? 'granted' : 'denied'}`);
    return allowed;
  } catch (error) {
    console.error("[AccessControl] Firebase check failed:", error);

    // Fallback: If Firebase fails but we have any cached data (even expired), use it
    if (cached) {
      try {
        const { value } = JSON.parse(cached);
        console.log("[AccessControl] Using expired cache due to Firebase error");
        return value;
      } catch (e) {
        // ignore parse errors
      }
    }

    return false;
  }
}
